"use client";

import { LAPYME_WHATSAPP } from "@workspace/shared/contact";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { BookOpen, HelpCircle, Newspaper, Video } from "lucide-react";
import { useEffect, useRef, useState, useTransition } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { acknowledgeLatestNovedades } from "./novedades.actions";
import { NOVEDADES_URL, type NovedadItem } from "./novedades-rss";

const UNREAD_DOT_REVEAL_DELAY_MS = 3000;
const UNREAD_DOT_CLOSE_DELAY_MS = 180;

type HelpMenuProps = {
  hasUnreadNovedades?: boolean;
  novedades?: NovedadItem[];
  orgSlug: string;
  showScheduleCall?: boolean;
  unreadNovedadesVersions?: string[];
};

export function HelpMenu({
  hasUnreadNovedades = false,
  novedades = [],
  orgSlug,
  showScheduleCall = false,
  unreadNovedadesVersions = [],
}: HelpMenuProps) {
  const unreadNovedadesVersionKey = hasUnreadNovedades
    ? unreadNovedadesVersions.join("\n")
    : "";
  const initialVisibleUnreadVersions = unreadNovedadesVersionKey
    ? unreadNovedadesVersionKey.split("\n")
    : [];
  const [open, setOpen] = useState(false);
  const [showUnreadDot, setShowUnreadDot] = useState(hasUnreadNovedades);
  const [unreadDotOpen, setUnreadDotOpen] = useState(false);
  const [visibleUnreadVersions, setVisibleUnreadVersions] = useState(
    initialVisibleUnreadVersions
  );
  const hideUnreadDotTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (hideUnreadDotTimeoutRef.current) {
      clearTimeout(hideUnreadDotTimeoutRef.current);
      hideUnreadDotTimeoutRef.current = null;
    }

    setShowUnreadDot(hasUnreadNovedades);
    setUnreadDotOpen(false);
    setVisibleUnreadVersions(
      unreadNovedadesVersionKey ? unreadNovedadesVersionKey.split("\n") : []
    );
  }, [hasUnreadNovedades, unreadNovedadesVersionKey]);

  useEffect(() => {
    if (!showUnreadDot) {
      setUnreadDotOpen(false);
      return;
    }

    const revealTimeout = setTimeout(() => {
      setUnreadDotOpen(true);
    }, UNREAD_DOT_REVEAL_DELAY_MS);

    return () => {
      clearTimeout(revealTimeout);
    };
  }, [showUnreadDot]);

  useEffect(
    () => () => {
      if (hideUnreadDotTimeoutRef.current) {
        clearTimeout(hideUnreadDotTimeoutRef.current);
      }
    },
    []
  );

  function hideUnreadDot() {
    setUnreadDotOpen(false);

    if (hideUnreadDotTimeoutRef.current) {
      clearTimeout(hideUnreadDotTimeoutRef.current);
    }

    hideUnreadDotTimeoutRef.current = setTimeout(() => {
      setShowUnreadDot(false);
      hideUnreadDotTimeoutRef.current = null;
    }, UNREAD_DOT_CLOSE_DELAY_MS);
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);

    if (!nextOpen) {
      setVisibleUnreadVersions([]);
      return;
    }

    if (!(nextOpen && showUnreadDot)) {
      return;
    }

    hideUnreadDot();
    startTransition(() => {
      acknowledgeLatestNovedades(orgSlug).catch(() => {
        // Keep the release note affordance quiet; the next server render can
        // restore the dot if the acknowledgement did not persist.
      });
    });
  }

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger
        render={
          <Button
            aria-label="Ayuda"
            className="relative size-8 rounded-full before:absolute before:-inset-1.5 before:content-['']"
            size="icon"
            variant="outline"
          />
        }
      >
        <HelpCircle className="hidden size-4 md:inline" />
        <FaWhatsapp className="size-4 md:hidden" />
        {showUnreadDot && (
          <span
            aria-hidden="true"
            className="t-badge -top-1 -right-1"
            data-open={unreadDotOpen}
          >
            <span className="t-badge-dot size-2.5 rounded-full bg-chart-2 ring-2 ring-background" />
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 space-y-4">
        <DropdownMenuGroup>
          <DropdownMenuItem
            render={
              <a
                aria-label="Escribinos por WhatsApp"
                href={LAPYME_WHATSAPP.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className="sr-only">Escribinos por WhatsApp</span>
                <FaWhatsapp className="size-4" />
                Escribinos por WhatsApp
              </a>
            }
          />
          {showScheduleCall && (
            <DropdownMenuItem
              render={
                <a
                  aria-label="Agendar videollamada"
                  href="https://lapyme.com.ar/agendar-videollamada"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className="sr-only">Agendar videollamada</span>
                  <Video className="size-4" />
                  Agendar videollamada
                </a>
              }
            />
          )}
          <DropdownMenuItem
            render={
              <a
                aria-label="Centro de ayuda"
                href="https://docs.lapyme.com.ar"
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className="sr-only">Centro de ayuda</span>
                <BookOpen className="size-4" />
                Centro de ayuda
              </a>
            }
          />
        </DropdownMenuGroup>
        {novedades.length > 0 && (
          <DropdownMenuGroup>
            <DropdownMenuLabel>Novedades</DropdownMenuLabel>
            <div className="relative">
              <div className="absolute top-4.5 bottom-[18px] left-[14px] w-0.5 -translate-x-1/2 bg-border" />
              {novedades.map((novedad) => (
                <DropdownMenuItem
                  className="relative grid grid-cols-[1.25rem_1fr] gap-2 py-2 pr-2 pl-1"
                  key={novedad.href}
                  render={
                    <a
                      aria-label={novedad.title}
                      href={novedad.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span className="relative z-10 place-self-center rounded-full p-1">
                        <span
                          className={`block size-2 rounded-full ${
                            visibleUnreadVersions.includes(novedad.href)
                              ? "bg-chart-2"
                              : "bg-muted-foreground"
                          }`}
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium">
                          {novedad.title}
                        </span>
                      </span>
                    </a>
                  }
                />
              ))}
              <DropdownMenuItem
                className="relative grid grid-cols-[1.25rem_1fr] gap-2 py-2 pr-2 pl-1"
                render={
                  <a
                    aria-label="Ver novedades"
                    href={NOVEDADES_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <span className="relative z-10 grid place-items-center">
                      <Newspaper className="size-4 text-muted-foreground" />
                    </span>
                    <span className="font-medium">Ver novedades</span>
                  </a>
                }
              />
            </div>
          </DropdownMenuGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
